// src/pages/DashboardPage.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Autocomplete,
  Avatar,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Search as SearchIcon, 
  Apartment as ApartmentIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Chat, ChatType, UserRole, User, Hotel, MessageType } from '../types/chat';
import { useTranslation } from 'react-i18next';

type ChatFilter = 'person' | 'room' | 'group' | 'staff';

const dedupeUsersById = (users: User[]): User[] => {
  const uniqueUsers = new Map<string, User>();

  users.forEach((u) => {
    if (!uniqueUsers.has(u.id)) {
      uniqueUsers.set(u.id, u);
    }
  });

  return Array.from(uniqueUsers.values());
};

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const openMobileDrawer = () => window.dispatchEvent(new Event('pms-mobile-drawer-open'));
  const [chats, setChats] = useState<Chat[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [chatName, setChatName] = useState('');
  const [chatType, setChatType] = useState<ChatType>(ChatType.ONE_ON_ONE);
  const [searchTerm, setSearchTerm] = useState('');
  const [chatFilter, setChatFilter] = useState<ChatFilter>('person');
  const [showArchived, setShowArchived] = useState(false);
  const [errorMessage, setErrorMessage] = useState(''); // For error display
  
  // Track chats with unread messages (Map of chat ID -> unread count)
  // Load from localStorage on mount
  const [unreadChats, setUnreadChats] = useState<Map<string, number>>(() => {
    try {
      const saved = localStorage.getItem('unreadChats');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Map(Object.entries(parsed).map(([k, v]) => [k, v as number]));
      }
    } catch (error) {
      console.error('Error loading unread chats from localStorage:', error);
    }
    return new Map();
  });
  
  // Track last seen message ID for each chat to avoid counting duplicates
  const [lastSeenMessageIds, setLastSeenMessageIds] = useState<Map<string, string>>(() => {
    try {
      const saved = localStorage.getItem('lastSeenMessageIds');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Map(Object.entries(parsed).map(([k, v]) => [k, v as string]));
      }
    } catch (error) {
      console.error('Error loading lastSeenMessageIds from localStorage:', error);
    }
    return new Map();
  });
  
  // New state for group chat participant selection
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<User[]>([]);
  const [selectedGuest, setSelectedGuest] = useState<User | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<any | null>(null);
  const [operatorRoomIds, setOperatorRoomIds] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [rooms, setRooms] = useState<any[]>([]);
  const [hotelInfo, setHotelInfo] = useState<Hotel | null>(null);
  const [hotelLoading, setHotelLoading] = useState(false);
  const roomNumberById = useMemo(() => {
    return rooms.reduce<Record<string, string>>((acc, room) => {
      if (room?.id && room?.roomNumber) {
        acc[room.id] = room.roomNumber;
      }
      return acc;
    }, {});
  }, [rooms]);
  
  // Use ref for synchronous tracking to prevent multiple clicks
  const isCreatingRef = useRef(false);
  const createButtonRef = useRef<HTMLButtonElement>(null); // Ref to immediately disable button
  const hasInitializedUnreads = useRef(false); // Track if we've initialized unread counts
  
  const navigate = useNavigate();
  const location = useLocation();
  const { connection } = useSocket();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
                      borderRadius: 8,
      backgroundColor: isDarkMode ? theme.palette.background.paper : '#ffffff',
      '&:hover': {
        backgroundColor: isDarkMode ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : '#cbd5e1',
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDarkMode ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: '#667eea',
          borderWidth: 2,
        },
      },
      '& fieldset': {
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : '#e2e8f0',
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: isDarkMode ? theme.palette.text.secondary : '#64748b',
      '&.Mui-focused': {
        color: '#667eea',
        fontWeight: 500,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
  };

  const resolveRoomNumber = useCallback(
    (chat: Chat) => {
      if (chat.room?.roomNumber) {
        return chat.room.roomNumber;
      }
      if (chat.roomId && roomNumberById[chat.roomId]) {
        return roomNumberById[chat.roomId];
      }
      if (chat.reservation?.roomId && roomNumberById[chat.reservation.roomId]) {
        return roomNumberById[chat.reservation.roomId];
      }
      if (chat.createdBy?.roomId && roomNumberById[chat.createdBy.roomId]) {
        return roomNumberById[chat.createdBy.roomId];
      }
      return null;
    },
    [roomNumberById],
  );

  const isNightModeActive = useCallback(() => {
    const settings = hotelInfo?.settings;
    if (!settings || !settings.nightModeEnabled) {
      return false;
    }

    const start = settings.nightModeStartTime || '18:00';
    const end = settings.nightModeEndTime || '08:00';

    const toMinutes = (time: string) => {
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m;
    };

    const startMinutes = toMinutes(start);
    const endMinutes = toMinutes(end);
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    if (startMinutes > endMinutes) {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }, [hotelInfo?.settings]);

  const formatRelativeTime = useCallback((dateStr?: string) => {
    if (!dateStr) {
      return '';
    }
    const diff = Date.now() - new Date(dateStr).getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diff < minute) return 'Şimdi';
    if (diff < hour) return `${Math.floor(diff / minute)} dk`;
    if (diff < day) return `${Math.floor(diff / hour)} s`;

    return new Date(dateStr).toLocaleDateString();
  }, []);

  const getChatInitials = (chat: Chat) => {
    const parts = chat.name.replace(/ - (Personal Chat|Room Chat|Group Chat|Staff Chat)$/i, '').split(' ');
    const initials = parts.slice(0, 2).map((p) => p.charAt(0)).join('');
    return initials || '??';
  };

  // Generate a unique color for each chat based on chat ID
  const getChatColor = (chatId: string) => {
    // Color palette with various gradients
    const colors = [
      'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', // Blue
      'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', // Orange
      'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', // Pink
      'linear-gradient(135deg, #10b981 0%, #34d399 100%)', // Green
      'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', // Purple
      'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', // Red
      'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)', // Cyan
      'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', // Amber
      'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', // Indigo
      'linear-gradient(135deg, #14b8a6 0%, #5eead4 100%)', // Teal
      'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)', // Violet
      'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)', // Rose
    ];
    
    // Hash function to convert chat ID to a number
    let hash = 0;
    for (let i = 0; i < chatId.length; i++) {
      hash = chatId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Use absolute value and modulo to get index
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Extract base color from gradient for border/background
  const getChatBaseColor = (chatId: string) => {
    const gradient = getChatColor(chatId);
    // Extract first color from gradient
    const match = gradient.match(/#[0-9a-fA-F]{6}/);
    return match ? match[0] : '#3b82f6';
  };

  // Convert hex color to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Helper function to check if a chat is archived
  const isChatArchived = (chat: Chat): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Helper to check if a date is in the past
    const isDatePast = (dateStr: string): boolean => {
      const date = new Date(dateStr);
      return date < today;
    };

    // For room chats or group chats, check if ALL members (guests) have checked out
    if (chat.type === ChatType.ROOM_CHAT && chat.room && chat.room.guests) {
      // Filter to only guest users (exclude operators and admins)
      const guests = chat.room.guests.filter(
        user => user.role === UserRole.GUEST
      );

      // If no guests in the room, not archived
      if (guests.length === 0) {
        return false;
      }

      // Check if ALL guests have reservations with past checkout dates
      const allGuestsCheckedOut = guests.every(guest => {
        if (!guest.reservation || !guest.reservation.checkOutDate) {
          return false; // If no reservation or checkout date, guest is considered still checked in
        }
        return isDatePast(guest.reservation.checkOutDate);
      });

      return allGuestsCheckedOut;
    }

    // For one-on-one chats, check the creator's reservation
    if (chat.type === ChatType.ONE_ON_ONE) {
      if (!chat.reservation || !chat.reservation.checkOutDate) {
        return false;
      }
      return isDatePast(chat.reservation.checkOutDate);
    }

    // For group chats, check the creator's reservation (can be enhanced later with participants)
    if (chat.type === ChatType.GROUP_CHAT && !chat.isAdminOnly && !chat.isStaffOnly) {
      if (!chat.reservation || !chat.reservation.checkOutDate) {
        return false;
      }
      return isDatePast(chat.reservation.checkOutDate);
    }

    // Staff chats are never archived
    return false;
  };

  // Get current chat ID from URL
  const currentChatId = location.pathname.includes('/dashboard/chat/') 
    ? location.pathname.split('/').pop() 
    : null;
  const welcomeSparkles = useMemo(() => ([
    { top: '8%', left: '24%', size: 6, opacity: 0.25 },
    { top: '14%', right: '18%', size: 7, opacity: 0.2 },
    { bottom: '12%', left: '18%', size: 5, opacity: 0.3 },
    { bottom: '18%', right: '12%', size: 6, opacity: 0.25 },
    { top: '36%', left: '-4%', size: 4, opacity: 0.3 },
    { top: '32%', right: '-6%', size: 4, opacity: 0.28 },
    { bottom: '-4%', left: '42%', size: 6, opacity: 0.25 },
    { top: '-6%', right: '42%', size: 6, opacity: 0.3 },
  ]), []);

  // Initialize hasInitializedUnreads if we have data in localStorage
  useEffect(() => {
    if (unreadChats.size > 0 || lastSeenMessageIds.size > 0) {
      hasInitializedUnreads.current = true;
    }
  }, [unreadChats.size, lastSeenMessageIds.size]);
  
  // Save unread chats to localStorage whenever it changes
  useEffect(() => {
    try {
      const obj = Object.fromEntries(unreadChats);
      localStorage.setItem('unreadChats', JSON.stringify(obj));
    } catch (error) {
      console.error('Error saving unread chats to localStorage:', error);
    }
  }, [unreadChats]);
  
  // Save lastSeenMessageIds to localStorage whenever it changes
  useEffect(() => {
    try {
      const obj = Object.fromEntries(lastSeenMessageIds);
      localStorage.setItem('lastSeenMessageIds', JSON.stringify(obj));
    } catch (error) {
      console.error('Error saving lastSeenMessageIds to localStorage:', error);
    }
  }, [lastSeenMessageIds]);

  // Clear unread status when user navigates to a chat
  useEffect(() => {
    if (currentChatId) {
      // Find the chat to get its last message
      const chat = chats.find(c => c.id === currentChatId);
      
      setUnreadChats((prev) => {
        const newMap = new Map(prev);
        newMap.delete(currentChatId);
        return newMap;
      });
      
      // Update lastSeenMessageId for this chat (use last human message)
      if (chat && chat.messages && chat.messages.length > 0) {
        const lastHumanMessage = chat.messages
          .slice()
          .reverse()
          .find(msg => !msg.isFromLLM);
        
        if (lastHumanMessage) {
          setLastSeenMessageIds((prev) => {
            const newMap = new Map(prev);
            newMap.set(currentChatId, lastHumanMessage.id);
            return newMap;
          });
        }
      }
    }
  }, [currentChatId, chats]);

  // Set filter based on URL path
  useEffect(() => {
    // Don't change filter when on chat page - preserve current filter
    if (location.pathname.includes('/chat/')) {
      return;
    }
    
    if (location.pathname.includes('/archived')) {
      // Show archived chats
      setShowArchived(true);
      setChatFilter('person');
    } else if (location.pathname.includes('/assistant')) {
      // For operators, Holiday Assistant shows their room chats
      setShowArchived(false);
      if (user?.role === UserRole.OPERATOR) {
        setChatFilter('room');
      } else {
        setChatFilter('person');
      }
    } else if (location.pathname.includes('/staff')) {
      // Staff chats - direct messages between staff
      setShowArchived(false);
      setChatFilter('staff');
    } else if (location.pathname === '/dashboard') {
      // Regular dashboard - exclude archived chats
      // Don't reset filter here - let the navigation state handle it
      setShowArchived(false);
    }
  }, [location.pathname, user?.role]);

  // Handle filter state passed from navigation (e.g., from ChatPage back button)
  useEffect(() => {
    const state = location.state as { filter?: ChatFilter };
    if (state?.filter) {
      setChatFilter(state.filter);
      // Save to localStorage for persistence
      localStorage.setItem('chatFilter', state.filter);
      // Clear the state after using it
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.pathname === '/dashboard' && !location.pathname.includes('/chat/')) {
      // If no state passed but we're on dashboard, try to restore from localStorage
      const savedFilter = localStorage.getItem('chatFilter') as ChatFilter | null;
      if (savedFilter && ['person', 'room', 'group', 'staff'].includes(savedFilter)) {
        setChatFilter(savedFilter);
      }
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    fetchChats();
    
    // Listen for new messages and new chats to update the list in real-time
    if (connection) {
      const handleNewMessage = (message: any) => {
        console.log('📨 New message received:', { 
          chatId: message.chatId, 
          messageId: message.id,
          isFromLLM: message.isFromLLM, 
          senderId: message.senderId,
          currentUserId: user?.id 
        });
        
        // Mark chat as unread if:
        // 1. Message is not from the current user
        // 2. Chat is not currently open
        // 3. Message is not from AI (isFromLLM)
        const isChatOpen = currentChatId === message.chatId;
        const isOwnMessage = message.senderId === user?.id;
        const isAIMessage = message.isFromLLM === true;
        
        console.log('🔍 Check conditions:', { isChatOpen, isOwnMessage, isAIMessage });
        
        if (!isChatOpen && !isOwnMessage && !isAIMessage) {
          // Use a tracked messages Set to prevent counting the same message twice
          const trackingKey = `${message.chatId}-${message.id}`;
          const tracked = sessionStorage.getItem(`tracked-${trackingKey}`);
          
          if (!tracked) {
            console.log('✅ Incrementing unread count for chat:', message.chatId);
            sessionStorage.setItem(`tracked-${trackingKey}`, 'true');
            
            setUnreadChats((prev) => {
              const newMap = new Map(prev);
              const currentCount = newMap.get(message.chatId) || 0;
              newMap.set(message.chatId, currentCount + 1);
              return newMap;
            });
          } else {
            console.log('⏭️ Message already tracked, skipping:', trackingKey);
          }
        } else {
          console.log('❌ Message not counted (chat open, own message, or AI message)');
        }
        
        // Refresh chat list to update sorting
        fetchChats();
      };
      
      const handleNewChatCreated = (chat: any) => {
        console.log('New chat created:', chat);
        fetchChats(); // Refresh the chat list
      };
      
      connection.on('NewMessage', handleNewMessage);
      connection.on('NewChatCreated', handleNewChatCreated);
      
      return () => {
        connection.off('NewMessage', handleNewMessage);
        connection.off('NewChatCreated', handleNewChatCreated);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, currentChatId, user?.id]);

  useEffect(() => {
    if (!user?.hotelId) return;

    const fetchHotelInfo = async () => {
      try {
        setHotelLoading(true);
        const response = await api.get(`/hotels/${user.hotelId}`);
        setHotelInfo(response.data);
      } catch (error) {
        console.error('Error fetching hotel info:', error);
      } finally {
        setHotelLoading(false);
      }
    };

    fetchHotelInfo();
  }, [user?.hotelId]);

  // /rooms is only allowed for Admin, Operator, SuperAdmin (backend returns 403 for others)
  useEffect(() => {
    if (!user?.hotelId) return;
    const canFetchRooms = user.role === UserRole.ADMIN || user.role === UserRole.OPERATOR || user.role === UserRole.SUPER_ADMIN;
    if (!canFetchRooms) {
      setRooms([]);
      return;
    }
    const fetchRooms = async () => {
      try {
        const response = await api.get('/rooms');
        setRooms(response.data || []);
      } catch (error: any) {
        if (error?.response?.status === 403) {
          setRooms([]);
          return;
        }
        console.error('Error fetching rooms:', error);
      }
    };
    fetchRooms();
  }, [user?.hotelId, user?.role]);

  const fetchChats = async () => {
    try {
      // In a real app, this API would return latest message/last update timestamp
      const response = await api.get('/chats');
      // Sort chats by updatedAt (most recent activity first) to show active chats at top
      const sortedChats = response.data.sort((a: Chat, b: Chat) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      setChats(sortedChats);
      
      // Initialize unread count ONLY on first load (not on every fetchChats call)
      // This prevents re-counting messages every time we fetch chats
      if (!hasInitializedUnreads.current) {
        hasInitializedUnreads.current = true;
        
        // Check each chat's last message against lastSeenMessageIds
        sortedChats.forEach((chat: Chat) => {
          if (chat.messages && chat.messages.length > 0) {
            // Find the last human (non-AI) message
            const lastHumanMessage = chat.messages
              .slice()
              .reverse()
              .find(msg => !msg.isFromLLM);
            
            const lastSeenId = lastSeenMessageIds.get(chat.id);
            
            // If we have a different last human message than what we last saw, and it's not from the current user
            if (lastHumanMessage && lastHumanMessage.senderId !== user?.id && lastHumanMessage.id !== lastSeenId) {
              // Set initial unread count
              setUnreadChats((prev) => {
                const newMap = new Map(prev);
                newMap.set(chat.id, 1); // At least 1 unread
                return newMap;
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  };

  const fetchUsers = async () => {
    // Only fetch users if the user is an operator or admin
    if (!user || (user.role !== UserRole.OPERATOR && user.role !== UserRole.ADMIN)) {
      return;
    }
    
    try {
      setLoadingUsers(true);
      
      const roomsResponse = await api.get('/rooms');
      const allRooms = roomsResponse.data;
      setRooms(allRooms);
      
      // Determine operator room assignments if needed
      // Check both primary operator (operatorId) and many-to-many assignments (operators array)
      let calculatedOperatorRoomIds: string[] = [];
      if (user.role === UserRole.OPERATOR) {
        calculatedOperatorRoomIds = allRooms
          .filter((room: any) => 
            room.operatorId === user.id || 
            room.operators?.some((op: User) => op.id === user.id)
          )
          .map((room: any) => room.id);
        setOperatorRoomIds(calculatedOperatorRoomIds);
      } else {
        setOperatorRoomIds([]);
      }
      
      const [usersResponse, staffResponse] = await Promise.all([
        api.get('/users'),
        api.get('/users/staff').catch((error) => {
          console.error('Error fetching staff users:', error);
          return { data: [] };
        }),
      ]);
      
      const allCandidates: User[] = (usersResponse.data || []).filter((u: User) => u.id !== user.id);
      const guestCandidates: User[] = allCandidates.filter((u: User) => u.role === UserRole.GUEST);
      const staffCandidates: User[] = (staffResponse.data || []).filter((u: User) => u.id !== user.id);
      
      let selectableGuests = guestCandidates;
      if (user.role === UserRole.OPERATOR && calculatedOperatorRoomIds.length > 0) {
        selectableGuests = guestCandidates.filter(
          (u: User) => u.roomId && calculatedOperatorRoomIds.includes(u.roomId)
        );
      }
      
      const combinedUsers =
        user.role === UserRole.OPERATOR
          ? [...selectableGuests, ...staffCandidates]
          : [...guestCandidates, ...staffCandidates];
      
      setAvailableUsers(dedupeUsersById(combinedUsers));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpenDialog = () => {
    // Reset creating state when opening dialog
    isCreatingRef.current = false;
    setCreatingChat(false);
    setErrorMessage(''); // Clear any previous errors
    setOpenDialog(true);
    // Fetch users when dialog opens (for participant/guest selection)
    if (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN) {
      fetchUsers();
    }
  };

  const handleChatClick = (chatId: string) => {
    // Find the chat to get its last message
    const chat = chats.find(c => c.id === chatId);
    
    // Save current filter to localStorage before navigating to chat
    localStorage.setItem('chatFilter', chatFilter);
    
    // Don't change filter when clicking - keep current filter selection
    
    // Clear unread status for this chat
    setUnreadChats((prev) => {
      const newMap = new Map(prev);
      newMap.delete(chatId);
      return newMap;
    });
    
    // Clear tracking for this chat's messages (so they can be counted again if chat is closed)
    // This is handled by sessionStorage which persists for the session
    
    // Update lastSeenMessageId for this chat (use last human message)
    if (chat && chat.messages && chat.messages.length > 0) {
      const lastHumanMessage = chat.messages
        .slice()
        .reverse()
        .find(msg => !msg.isFromLLM);
      
      if (lastHumanMessage) {
        setLastSeenMessageIds((prev) => {
          const newMap = new Map(prev);
          newMap.set(chatId, lastHumanMessage.id);
          return newMap;
        });
      }
    }
    
    // Navigate to chat and pass current filter so we can restore it when coming back
    navigate(`/dashboard/chat/${chatId}`, { state: { previousFilter: chatFilter } });
  };

  const chatsAfterSearch = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return chats
      .filter((chat) => chat.name.toLowerCase().includes(term))
      .filter((chat) => (showArchived ? isChatArchived(chat) : !isChatArchived(chat)));
  }, [chats, searchTerm, showArchived]);

  const personChats = useMemo(
    () =>
      chatsAfterSearch.filter(
        (chat) =>
          chat.type === ChatType.ONE_ON_ONE &&
          !chat.isAdminOnly &&
          !chat.isStaffOnly,
      ),
    [chatsAfterSearch],
  );

  const roomChats = useMemo(
    () =>
      chatsAfterSearch.filter(
        (chat) =>
          chat.type === ChatType.ROOM_CHAT &&
          !chat.isAdminOnly &&
          !chat.isStaffOnly,
      ),
    [chatsAfterSearch],
  );

  const groupChats = useMemo(
    () =>
      chatsAfterSearch.filter(
        (chat) =>
          chat.type === ChatType.GROUP_CHAT &&
          !chat.isAdminOnly &&
          !chat.isStaffOnly,
      ),
    [chatsAfterSearch],
  );

  const staffChats = useMemo(
    () => chatsAfterSearch.filter((chat) => chat.isAdminOnly || chat.isStaffOnly),
    [chatsAfterSearch],
  );

  const filteredChats = useMemo(() => {
    switch (chatFilter) {
      case 'person':
        return personChats;
      case 'room':
        return roomChats;
      case 'group':
        return groupChats;
      case 'staff':
        return staffChats;
      default:
        return chatsAfterSearch;
    }
  }, [chatFilter, personChats, roomChats, groupChats, staffChats, chatsAfterSearch]);

  const tabData = useMemo(() => {
    const baseTabs = [
      { value: 'person', label: t('dashboard.peopleTab', 'Kişiler'), count: personChats.length },
      { value: 'room', label: t('dashboard.roomsTab', 'Odalar'), count: roomChats.length },
      { value: 'group', label: t('dashboard.groupsTab', 'Gruplar'), count: groupChats.length },
    ];

    if (user?.role !== UserRole.GUEST) {
      baseTabs.push({ value: 'staff', label: t('dashboard.staffTab', 'Personel'), count: staffChats.length });
    }

    return baseTabs;
  }, [personChats.length, roomChats.length, groupChats.length, staffChats.length, user?.role, t]);

  // Helper to reset all dialog states in one place
  const resetDialogState = () => {
    setChatName('');
    setChatType(ChatType.ONE_ON_ONE);
    setSelectedParticipants([]);
    setSelectedGuest(null);
    setSelectedRoom(null);
    setAiEnabled(true);
    setErrorMessage('');
    isCreatingRef.current = false;
    setCreatingChat(false);
    if (createButtonRef.current) {
      createButtonRef.current.disabled = false; // Reset button enabled
    }
  };

  const nightModeOn = isNightModeActive();
  const hotelNameDisplay = hotelInfo?.name || t('dashboard.hotelPlaceholder', 'Otel');
  const aiModeStatusText = hotelLoading
    ? t('dashboard.loadingShort', '...')
    : (nightModeOn ? t('dashboard.on', 'On') : t('dashboard.off', 'Off'));
  const aiModeLabel = t('dashboard.aiModeLabel', 'AI Modu');
  const roomNumberLabel = t('dashboard.roomNumberLabel', 'Oda no');

  return (
    // DashboardPage now acts as the Chat List + Welcome/Placeholder Pane
    <Grid container sx={{ flexGrow: 1, height: '100%', m: 0, p: 0, bgcolor: isDarkMode ? theme.palette.background.default : '#EEF2F7' }}>
      {/* Middle Pane: Chat List (Mockup's middle column) */}
      <Grid size={{ xs: 12, md: 5, lg: 4 }} sx={{ 
        height: '100%', 
        p: isMobile ? 0 : 1.5,
        bgcolor: isDarkMode ? theme.palette.background.default : '#EEF2F7',
        display: isMobile && currentChatId ? 'none' : 'block',
      }}>
        <Paper sx={{ 
          height: '100%', 
          p: isMobile ? 0 : 2.5, 
          borderRadius: isMobile ? 0 : '16px',
          boxShadow: isMobile ? 'none' : (isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(21,41,77,.05)'),
          bgcolor: isMobile ? (isDarkMode ? theme.palette.background.default : '#EEF2F7') : theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <Box
            sx={{
              p: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 12px) 14px 12px' : 2,
              borderRadius: isMobile ? 0 : '16px',
              border: isMobile ? 'none' : (isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #E4E9F1'),
              borderBottom: isMobile ? (isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #E4E9F1') : undefined,
              background: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              mb: isMobile ? 0 : 2.5,
              boxShadow: isMobile ? '0 1px 0 #E4E9F1' : (isDarkMode ? '0 6px 20px rgba(0, 0, 0, 0.3)' : '0 1px 0 #E4E9F1'),
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                {isMobile && (
                  <IconButton
                    onClick={openMobileDrawer}
                    aria-label="Menü"
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '12px',
                      bgcolor: isDarkMode ? theme.palette.action.hover : '#F1F5FA',
                      color: '#15294D',
                      flexShrink: 0,
                      '&:hover': { bgcolor: isDarkMode ? theme.palette.action.selected : '#E3EBF5' },
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                )}
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg,#1FB6FF,#189BF0)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ApartmentIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px', color: isDarkMode ? theme.palette.text.primary : '#15294D', lineHeight: 1.2 }}>
                    {hotelNameDisplay}
                  </Typography>
                  {user?.role !== UserRole.GUEST && (
                    <Typography sx={{ fontSize: '12.5px', color: theme.palette.text.secondary, fontWeight: 500, mt: '1px' }}>
                      {aiModeLabel}: <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{aiModeStatusText}</Box>
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: isMobile ? 1.75 : 2, alignItems: 'center' }}>
              {user?.role !== UserRole.GUEST && (
                <IconButton
                  onClick={handleOpenDialog}
                  size="small"
                  sx={{ 
                    backgroundColor: '#189BF0',
                    color: '#ffffff',
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    flexShrink: 0,
                    boxShadow: '0 4px 12px rgba(24,155,240,.35)',
                    '&:hover': {
                      backgroundColor: '#1586d0',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                overflowX: 'auto', 
                flex: 1,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}>
              {tabData.map((tab) => {
                const isActive = chatFilter === tab.value;
                return (
                  <Box
                    key={tab.value}
                    onClick={() => setChatFilter(tab.value as ChatFilter)}
                    sx={{
                      flex: 1,
                      minWidth: 78,
                      px: 1,
                      py: '10px',
                      borderRadius: 999,
                      border: `1px solid ${isActive ? '#189BF0' : (isDarkMode ? theme.palette.divider : '#E4E9F1')}`,
                      backgroundColor: isActive 
                        ? (isDarkMode ? 'rgba(24, 155, 240, 0.2)' : '#EAF6FE')
                        : theme.palette.background.paper,
                      color: isActive 
                        ? '#189BF0'
                        : theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: '13.5px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 600 }}>
                      {tab.label}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        minWidth: 20,
                        height: 20,
                        borderRadius: 999,
                        backgroundColor: isActive ? '#189BF0' : (isDarkMode ? theme.palette.action.selected : '#EDF1F7'),
                        color: isActive ? '#fff' : theme.palette.text.secondary,
                        fontSize: '11.5px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5,
                        lineHeight: 1,
                      }}
                    >
                      {tab.count}
                    </Box>
                  </Box>
                );
              })}
              </Box>
            </Box>

            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('dashboard.searchPlaceholder', 'Oda veya isim ara...')}
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#A9B2C3', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 1.5,
                mb: isMobile ? 0 : 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  bgcolor: isDarkMode ? theme.palette.action.hover : '#F4F7FB',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: isDarkMode ? theme.palette.divider : '#E4E9F1',
                  },
                  '&:hover fieldset': {
                    borderColor: '#189BF0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#189BF0',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? '14px' : 0.5, py: isMobile ? 1.5 : 0, pb: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 12px)' : 0 }}>
            {filteredChats.length === 0 ? (
              <Typography 
                variant="body2" 
                sx={{ 
                  textAlign: 'center', 
                  mt: 6,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                {chats.length === 0 ? t('dashboard.noChatsAvailable') : t('dashboard.noChatsFound')}
              </Typography>
            ) : (
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {filteredChats.map((chat) => {
                  const lastHumanMessage = chat.messages
                    ?.slice()
                    .reverse()
                    .find((msg) =>
                      !msg.isFromLLM &&
                      msg.type !== MessageType.SYSTEM &&
                      !/has joined this chat/i.test(msg.content || '')
                    );

                  const preview = lastHumanMessage
                    ? lastHumanMessage.content
                    : t('dashboard.noMessages');

                  const resolvedRoomNumber = resolveRoomNumber(chat);
                  // Don't show room number for staff chats or for virtual room 000 (guests without physical room)
                  const isStaffChat = chat.isAdminOnly || chat.isStaffOnly;
                  const showRoomNumber = resolvedRoomNumber && resolvedRoomNumber !== '000';
                  const chipLabel = isStaffChat
                    ? (chat.type === ChatType.GROUP_CHAT
                        ? t('dashboard.groupChatLabel', 'Grup')
                        : t('dashboard.chat', 'Sohbet'))
                    : showRoomNumber
                      ? `${roomNumberLabel}: ${resolvedRoomNumber}`
                      : chat.type === ChatType.GROUP_CHAT
                        ? t('dashboard.groupChatLabel', 'Grup')
                        : t('dashboard.chat', 'Sohbet');

                  const unreadCount = unreadChats.get(chat.id) || 0;
                  const chatBaseColor = getChatBaseColor(chat.id);
                  const isSelected = currentChatId === chat.id;

                  return (
                    <ListItem
                      key={chat.id}
                      disableGutters
                      sx={{ p: 0 }}
                    >
                      <ListItemButton
                        onClick={() => handleChatClick(chat.id)}
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: '14px',
                          py: '13px',
                          borderRadius: '14px',
                          border: 'none',
                          borderLeft: `4px solid ${isSelected ? chatBaseColor : '#9B5CE0'}`,
                          backgroundColor: isSelected 
                            ? (isDarkMode ? hexToRgba(chatBaseColor, 0.15) : '#F4ECFD')
                            : (isDarkMode ? theme.palette.background.paper : '#FBF7FF'),
                          boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(21,41,77,.05)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isDarkMode ? hexToRgba(chatBaseColor, 0.12) : '#F4ECFD',
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 46,
                            height: 46,
                            fontWeight: 800,
                            fontSize: '15px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg,#B37BF0,#9B5CE0)',
                            color: '#fff',
                          }}
                        >
                          {getChatInitials(chat)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: isDarkMode ? theme.palette.text.primary : '#15294D',
                                fontSize: '15px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {chat.name.replace(/ - (Personal Chat|Room Chat|Group Chat|Staff Chat)$/i, '')}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, fontWeight: 500, flexShrink: 0, mt: '2px' }}>
                              {formatRelativeTime(lastHumanMessage?.createdAt || chat.updatedAt)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '12.5px', color: theme.palette.text.secondary, mt: '1px' }}>
                            {chipLabel}
                            {unreadCount > 0 ? ` · ${unreadCount}` : ''}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#9AA3B5',
                              mt: '2px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: unreadCount ? 600 : 400,
                              fontSize: '12.5px',
                            }}
                          >
                            {preview}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>
      </Grid>
      
      {/* Right Pane: Chat Content or Welcome/Placeholder */}
      <Grid size={{ xs: 12, md: 7, lg: 8 }} sx={{
        height: '100%',
        p: isMobile ? 0 : 1.5,
        bgcolor: isDarkMode ? theme.palette.background.default : '#EEF2F7',
        display: isMobile && !currentChatId ? 'none' : 'block',
      }}>
        {currentChatId ? (
          <Box sx={{ height: isMobile ? '100%' : '95%', borderRadius: isMobile ? 0 : 6, overflow: 'hidden', boxShadow: isMobile ? 'none' : (isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'), mt: isMobile ? 0 : 2, ml: isMobile ? 0 : -2 }}>
            <Outlet />
          </Box>
        ) : (
          <Paper 
            sx={{ 
              p: 4, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              borderRadius: 12, 
              boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
              bgcolor: isDarkMode ? theme.palette.background.default : '#f5f7fa',
            }}
          >
            <Box
              sx={{
                p: 4,
                borderRadius: 8,
                background: isDarkMode ? theme.palette.background.default : '#f5f7fa',
                boxShadow: isDarkMode ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #e2e8f0',
                textAlign: 'center',
                maxWidth: 520,
              }}
            >
              <Box sx={{ position: 'relative', width: 240, height: 240, mb: 3, mx: 'auto' }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: `2px dashed ${isDarkMode ? 'rgba(148,163,184,0.4)' : 'rgba(148,163,184,0.3)'}`,
                    background: isDarkMode 
                      ? 'radial-gradient(circle, rgba(148,163,184,0.08) 0%, rgba(15,23,42,0.3) 100%)'
                      : 'radial-gradient(circle, rgba(226,232,240,0.4) 0%, rgba(248,250,252,0.8) 100%)',
                    position: 'relative',
                    overflow: 'visible',
                    mx: 'auto',
                  }}
                >
                  {welcomeSparkles.map((sparkle, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'absolute',
                        width: sparkle.size,
                        height: sparkle.size,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, rgba(99, 102, 241, ${sparkle.opacity}) 0%, rgba(14, 165, 233, ${sparkle.opacity}) 100%)`,
                        top: sparkle.top,
                        left: sparkle.left,
                        right: sparkle.right,
                        bottom: sparkle.bottom,
                      }}
                    />
                  ))}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '52%',
                      transform: 'translate(-50%, -50%)',
                      width: 115,
                      height: 150,
                      borderRadius: 8,
                      border: `2px solid ${isDarkMode ? 'rgba(148,163,184,0.5)' : '#cbd5f5'}`,
                      background: isDarkMode ? '#111826' : '#ffffff',
                      boxShadow: isDarkMode 
                        ? '0 6px 15px rgba(0,0,0,0.35)' 
                        : '0 6px 15px rgba(15,23,42,0.1)',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 24,
                        left: '50%',
                        width: 60,
                        height: 6,
                        background: isDarkMode ? '#94a3b8' : '#cbd5f5',
                        transform: 'translateX(-50%) rotate(-5deg)',
                        borderRadius: 12,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: '50%',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: `2px solid ${isDarkMode ? 'rgba(148,163,184,0.4)' : '#cbd5f5'}`,
                        transform: 'translateX(-50%)',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -14,
                      left: '50%',
                      width: '70%',
                      height: 34,
                      background: isDarkMode ? 'rgba(148,163,184,0.12)' : '#f8fafc',
                      borderRadius: '999px',
                      transform: 'translateX(-50%)',
                      border: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.25)' : 'rgba(148,163,184,0.4)'}`,
                    }}
                  />
                </Box>
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {t('dashboard.selectChatPrompt', 'Select a chat.')}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 400, maxWidth: 420 }}>
                {t('dashboard.selectChatDescription', 'Bir konuşma başlatmak veya yeni bir sohbet oluşturmak için sol kenar çubuğundan bir sohbet seçin.')}
              </Typography>
            </Box>
          </Paper>
        )}
      </Grid>
      
      {/* Create New Chat Dialog */}
      <Dialog 
        key="create-chat-dialog"
        open={openDialog} 
        onClose={(event, reason) => {
          // Prevent user-initiated closing (backdrop/escape) while creating chat
          // Note: Programmatic closing via setOpenDialog(false) does NOT trigger onClose
          if (isCreatingRef.current || creatingChat) {
            return;
          }
          setOpenDialog(false);
          resetDialogState(); // Use centralized reset
        }}
        disableEscapeKeyDown={creatingChat}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
                      borderRadius: 8,
            background: isDarkMode 
              ? theme.palette.background.paper 
              : '#ffffff',
            boxShadow: isDarkMode
              ? '0 4px 6px rgba(0, 0, 0, 0.3)'
              : '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: isDarkMode ? `1px solid ${theme.palette.divider}` : 'none',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          color: isDarkMode ? theme.palette.text.primary : '#1e293b',
          fontSize: '1.25rem',
        }}>
          {t('dashboard.createNewChat')}
        </DialogTitle>
        <DialogContent sx={{ 
          pt: 2,
          bgcolor: 'transparent',
          '& .MuiFormHelperText-root': {
            color: isDarkMode ? theme.palette.text.secondary : 'rgba(0, 0, 0, 0.6)',
          },
        }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t('dashboard.chatName')}
            fullWidth
            variant="outlined"
            value={chatName}
            onChange={(e) => setChatName(e.target.value.trim())} // Trim on change for better UX
            error={!chatName.trim()} // Visual feedback
            helperText={!chatName.trim() ? t('dashboard.chatNameRequired') : ''}
            sx={{ ...textFieldSx, mb: 2, mt: 1 }}
          />
          <FormControl fullWidth variant="outlined" sx={{ ...textFieldSx, mb: 2 }}>
            <InputLabel 
              id="chat-type-label"
              sx={{
                color: isDarkMode ? theme.palette.text.secondary : undefined,
                '&.Mui-focused': {
                  color: '#667eea',
                },
              }}
            >
              {t('dashboard.chatType')}
            </InputLabel>
            <Select
              labelId="chat-type-label"
              id="chat-type"
              value={chatType}
              onChange={(e) => {
                setChatType(e.target.value as ChatType);
                setSelectedParticipants([]); // Clear participants when type changes
                setSelectedGuest(null); // Clear selected guest when type changes
                setSelectedRoom(null); // Clear selected room when type changes
              }}
              label={t('dashboard.chatType')}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 8,
                    mt: 1,
                    bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                    boxShadow: isDarkMode
                      ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                      : '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: isDarkMode ? `1px solid ${theme.palette.divider}` : 'none',
                    '& .MuiMenuItem-root': {
                      color: isDarkMode ? theme.palette.text.primary : '#1f2937',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                      },
                      '&.Mui-selected': {
                        bgcolor: isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                        '&:hover': {
                          bgcolor: isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.15)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value={ChatType.ONE_ON_ONE}>{t('chat.guest')} {t('dashboard.chats')} ({t('dashboard.oneOnOne')})</MenuItem>
              <MenuItem value={ChatType.ROOM_CHAT}>{t('dashboard.room')} {t('dashboard.chats')}</MenuItem>
              <MenuItem value={ChatType.GROUP_CHAT}>{t('dashboard.group')} {t('dashboard.chats')}</MenuItem>
            </Select>
          </FormControl>

          {/* Show guest selection for ONE_ON_ONE chat when operator/admin */}
          {chatType === ChatType.ONE_ON_ONE && (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN) && (
            <>
              <Typography variant="body2" sx={{ 
                mb: 1,
                color: isDarkMode ? theme.palette.text.secondary : undefined,
              }}>
                {t('dashboard.selectGuest')}
              </Typography>
              
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  id="guest-select"
                  options={availableUsers.filter(u => u.role === UserRole.GUEST)}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                  value={selectedGuest}
                  onChange={(_, newValue) => setSelectedGuest(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('dashboard.selectGuestLabel')}
                      placeholder={t('dashboard.chooseGuest')}
                      error={!selectedGuest && (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN)} // Visual feedback
                      helperText={!selectedGuest && (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN) ? t('dashboard.guestRequired') : ''}
                      sx={{ ...textFieldSx, mb: 2 }}
                    />
                  )}
                  renderOption={(props, option) => {
                    // FIX: Extract key to avoid React warning
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Avatar sx={{ mr: 1, width: 32, height: 32, fontSize: '0.875rem' }}>
                            {option.firstName[0]}{option.lastName[0]}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{
                              color: isDarkMode ? theme.palette.text.primary : undefined,
                            }}>
                              {option.firstName} {option.lastName}
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: isDarkMode ? theme.palette.text.secondary : undefined,
                            }}>
                              Guest
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    );
                  }}
                  sx={{ mb: 2 }}
                  ListboxProps={{
                    sx: {
                      bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                      '& .MuiAutocomplete-option': {
                        color: isDarkMode ? theme.palette.text.primary : '#1f2937',
                        '&:hover': {
                          bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&[aria-selected="true"]': {
                          bgcolor: isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                        },
                      },
                    },
                  }}
                  PaperComponent={({ children, ...other }) => (
                    <Paper
                      {...other}
                      sx={{
                        bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                        boxShadow: isDarkMode
                          ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                          : '0 4px 6px rgba(0, 0, 0, 0.1)',
                        border: isDarkMode ? `1px solid ${theme.palette.divider}` : 'none',
                      }}
                    >
                      {children}
                    </Paper>
                  )}
                />
              )}
            </>
          )}

          {/* Show room selection for ROOM_CHAT when operator/admin */}
          {chatType === ChatType.ROOM_CHAT && (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN) && (
            <>
              <Typography variant="body2" sx={{ 
                mb: 1,
                color: isDarkMode ? theme.palette.text.secondary : undefined,
              }}>
                {user?.role === UserRole.OPERATOR ? 'Select Room' : 'Select Room (Optional)'}
              </Typography>
              
              <Autocomplete
                id="room-select"
                options={user?.role === UserRole.OPERATOR 
                  ? rooms.filter((room: any) => operatorRoomIds.includes(room.id))
                  : rooms}
                getOptionLabel={(option) => `Room ${option.roomNumber}${option.roomType ? ` - ${option.roomType}` : ''}`}
                value={selectedRoom}
                onChange={(_, newValue) => setSelectedRoom(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Room"
                    placeholder="Choose a room"
                    error={!selectedRoom && user?.role === UserRole.OPERATOR && operatorRoomIds.length > 0}
                    helperText={!selectedRoom && user?.role === UserRole.OPERATOR && operatorRoomIds.length > 0 
                      ? 'Room selection is required' 
                      : user?.role === UserRole.ADMIN 
                        ? 'Optional: Select a room to link this chat'
                        : ''}
                    sx={{ ...textFieldSx, mb: 2 }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{
                            color: isDarkMode ? theme.palette.text.primary : undefined,
                          }}>
                            Room {option.roomNumber}
                          </Typography>
                          {option.roomType && (
                            <Typography variant="caption" sx={{
                              color: isDarkMode ? theme.palette.text.secondary : undefined,
                            }}>
                              {option.roomType}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                sx={{ mb: 2 }}
                ListboxProps={{
                  sx: {
                    bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                    '& .MuiAutocomplete-option': {
                      color: isDarkMode ? theme.palette.text.primary : '#1f2937',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                      },
                      '&[aria-selected="true"]': {
                        bgcolor: isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                      },
                    },
                  },
                }}
                PaperComponent={({ children, ...other }) => (
                  <Paper
                    {...other}
                    sx={{
                      bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                      boxShadow: isDarkMode
                        ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      border: isDarkMode ? `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />
            </>
          )}

          {/* Show participant selection for GROUP_CHAT */}
          {chatType === ChatType.GROUP_CHAT && (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN) && (
            <>

              
              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  id="participants-select"
                  options={availableUsers}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.role}${option.roomId ? ` - Room` : ''})`}
                  value={selectedParticipants}
                  onChange={(_, newValue) => setSelectedParticipants(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('dashboard.selectParticipantsLabel')}
                      placeholder={t('dashboard.chooseParticipants')}
                      error={selectedParticipants.length === 0} // Visual feedback
                      helperText={selectedParticipants.length === 0 ? t('dashboard.atLeastOneParticipantRequired') : ''}
                      sx={textFieldSx}
                    />
                  )}
                  renderOption={(props, option) => {
                    // FIX: Extract key to avoid React warning
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Avatar sx={{ mr: 1, width: 32, height: 32, fontSize: '0.875rem' }}>
                            {option.firstName[0]}{option.lastName[0]}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{
                              color: isDarkMode ? theme.palette.text.primary : undefined,
                            }}>
                              {option.firstName} {option.lastName}
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: isDarkMode ? theme.palette.text.secondary : undefined,
                            }}>
                              {option.role}{option.roomId ? ` • Guest` : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        avatar={<Avatar>{option.firstName[0]}</Avatar>}
                        label={`${option.firstName} ${option.lastName}`}
                        size="small"
                        sx={{
                          bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.2)' : undefined,
                          color: isDarkMode ? theme.palette.text.primary : undefined,
                        }}
                      />
                    ))
                  }
                  sx={{ mb: 2 }}
                  ListboxProps={{
                    sx: {
                      bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                      '& .MuiAutocomplete-option': {
                        color: isDarkMode ? theme.palette.text.primary : '#1f2937',
                        '&:hover': {
                          bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&[aria-selected="true"]': {
                          bgcolor: isDarkMode ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)',
                        },
                      },
                    },
                  }}
                  PaperComponent={({ children, ...other }) => (
                    <Paper
                      {...other}
                      sx={{
                        bgcolor: isDarkMode ? theme.palette.background.paper : '#ffffff',
                        boxShadow: isDarkMode
                          ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                          : '0 4px 6px rgba(0, 0, 0, 0.1)',
                        border: isDarkMode ? `1px solid ${theme.palette.divider}` : 'none',
                      }}
                    >
                      {children}
                    </Paper>
                  )}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={aiEnabled}
                    onChange={(e) => setAiEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#667eea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ 
                    fontWeight: 600, 
                    color: isDarkMode ? theme.palette.text.primary : '#1f2937',
                  }}>
                    {t('dashboard.enableAIAssistant')}
                  </Typography>
                }
              />
              
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5, 
          gap: 1,
          borderTop: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid rgba(0, 0, 0, 0.05)',
        }}>
          <Button 
            onClick={() => {
              // Prevent canceling while creating chat
              if (isCreatingRef.current || creatingChat) {
                return;
              }
              setOpenDialog(false);
              resetDialogState(); // Use centralized reset
            }}
            disabled={isCreatingRef.current || creatingChat}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
                      borderRadius: 8,
              px: 2.5,
              color: isDarkMode ? theme.palette.text.secondary : '#64748b',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f1f5f9',
              },
              '&:disabled': {
                color: isDarkMode ? theme.palette.text.disabled : '#cbd5e1',
                cursor: 'not-allowed',
              },
            }}
          >
            {t('dashboard.cancel')}
          </Button>
          <Button 
            ref={createButtonRef} // Ref for immediate disable
            onClick={async (e) => {
              // Prevent event propagation
              e.preventDefault();
              e.stopPropagation();
              
              // ENHANCED GUARD: Check ref and button state synchronously
              if (isCreatingRef.current) {
                console.log('Blocked duplicate click - already creating (ref check)');
                return;
              }
              if (createButtonRef.current?.disabled) {
                console.log('Blocked duplicate click - button already disabled');
                return;
              }
              if (creatingChat) {
                console.log('Blocked duplicate click - already creating (state check)');
                return;
              }
              
              // Immediately set guards (synchronous)
              isCreatingRef.current = true;
              if (createButtonRef.current) {
                createButtonRef.current.disabled = true; // Instant UI disable
              }
              setCreatingChat(true);
              setErrorMessage(''); // Clear previous errors
              
              try {
                let response: any;
                
                // Validate chatName early (min length 1 after trim)
                if (!chatName.trim()) {
                  throw new Error('Chat name is required');
                }
                
                // If it's a group chat with participants selected, use custom group chat endpoint
                if (chatType === ChatType.GROUP_CHAT && selectedParticipants.length > 0) {
                  response = await api.post('/chats/group/custom', {
                    name: chatName.trim(),
                    participantIds: selectedParticipants.map(p => p.id),
                    aiEnabled: aiEnabled,
                    isLLMPresent: aiEnabled,
                  });
                } else if (chatType === ChatType.ONE_ON_ONE && selectedGuest) {
                  // Validate guest ID
                  if (!selectedGuest.id) {
                    throw new Error('Invalid guest selected');
                  }
                  
                  // For ONE_ON_ONE chats with selected guest, create chat first
                  response = await api.post('/chats', {
                    name: chatName.trim() || `Chat with ${selectedGuest.firstName} ${selectedGuest.lastName}`,
                    type: chatType,
                  });
                  
                  // Add the selected guest as a participant
                  if (response.data.id) {
                    console.log('Adding participant to chat:', response.data.id, 'with userId:', selectedGuest.id);
                    try {
                      await api.post(`/chats/${response.data.id}/participants`, {
                        userIds: [selectedGuest.id], // Fixed: Use plural 'userIds' as array
                      });
                      console.log('Participant added successfully');
                    } catch (participantError: any) {
                      console.error('Failed to add participant:', participantError.response?.data || participantError.message);
                      // Don't throw here—chat is created, but warn user
                      setErrorMessage(`Chat created, but failed to add guest: ${participantError.response?.data?.message || 'Server error'}. You can add them manually later.`);
                    }
                  }
                } else if (chatType === ChatType.ROOM_CHAT) {
                  // For ROOM_CHAT, validate room selection for operators
                  if (user?.role === UserRole.OPERATOR && operatorRoomIds.length > 0 && !selectedRoom) {
                    throw new Error('Please select a room');
                  }
                  
                  // Create room chat with roomId if selected
                  response = await api.post('/chats', {
                    name: chatName.trim(),
                    type: chatType,
                    roomId: selectedRoom?.id || null,
                  });
                } else {
                  // Use regular chat creation for other types
                  response = await api.post('/chats', {
                    name: chatName.trim(),
                    type: chatType,
                  });
                }
                
                // SUCCESS: Close dialog IMMEDIATELY (no timeout needed)
                setOpenDialog(false);
                
                // Reset and refresh
                resetDialogState(); // Centralized reset
                fetchChats();
                
                // Navigate to the newly created chat (no timeout; React handles it fine)
                if (response?.data?.id) {
                  navigate(`/dashboard/chat/${response.data.id}`);
                }
              } catch (error: any) {
                console.error('Error creating chat:', error); // Original log
                setErrorMessage(error.message || 'Failed to create chat. Please try again.'); // Show error in dialog
                // On error: Reset guards but KEEP DIALOG OPEN for retry
                isCreatingRef.current = false;
                setCreatingChat(false);
                if (createButtonRef.current) {
                  createButtonRef.current.disabled = false; // Re-enable button
                }
              }
            }}
            variant="contained"
            disabled={
              creatingChat ||
              !chatName.trim() || // Use trim for validation
              chatName.trim().length < 1 || // Explicit min length
              (chatType === ChatType.GROUP_CHAT && selectedParticipants.length === 0) ||
              (chatType === ChatType.ONE_ON_ONE && (user?.role === UserRole.OPERATOR || user?.role === UserRole.ADMIN) && !selectedGuest)
            }
            sx={{
              textTransform: 'none',
              fontWeight: 500,
                      borderRadius: 8,
              px: 2.5,
              bgcolor: '#667eea',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isDarkMode ? '#818cf8' : '#5568d3',
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: isDarkMode ? theme.palette.action.disabledBackground : '#cbd5e1',
                color: isDarkMode ? theme.palette.action.disabled : '#94a3b8',
                pointerEvents: 'none',
                cursor: 'not-allowed',
              },
              '&.Mui-disabled': {
                pointerEvents: 'none',
              },
            }}
          >
            {creatingChat ? t('dashboard.creating') : t('dashboard.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default DashboardPage;